import { DateTime } from 'luxon';
import React from 'react';

import { Campaign } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { EntityType } from 'common/types';

import { MAXIMUM_TIMESPAN } from './constants';
import { useCampaignPerformancePerDay } from '../../../../hooks/useCampaignPerformancePerDay';
import { AdCampaign } from '../../../../types';
import {
  trackCampaignPerformanceInteractionClickArrow,
  trackCampaignPerformanceInteractionClickSalesOrOrder,
} from '../../../../utils/telemetry/telemetry';
import { ChartCategory } from '../../../analytics-chart/types';

export const CHART_TIME_SPAN = 14;

export const useChartState = ({
  entityType,
  entityId,
  campaign,
}: {
  entityType: Extract<EntityType, 'venue' | 'merchant'>;
  entityId: string;
  campaign: Campaign | AdCampaign;
}) => {
  const [category, setCategory] = React.useState<ChartCategory>(ChartCategory.SALES);
  const [daysOffset, setDaysOffset] = React.useState(0);

  const campaignStartDate = DateTime.fromISO(campaign.start_date).startOf('day');
  const todayDate = DateTime.now().startOf('day');
  const earliestDate = DateTime.max(campaignStartDate, todayDate.minus(MAXIMUM_TIMESPAN));
  const latestDate = campaign.end_date
    ? DateTime.min(DateTime.fromISO(campaign.end_date).startOf('day'), todayDate)
    : todayDate;

  const toDate = latestDate.minus({ days: daysOffset });
  const fromDate = toDate.minus({ days: CHART_TIME_SPAN });

  const { campaignPerformancePerDay: chartData, isFetching: isLoading } = useCampaignPerformancePerDay({
    entityType,
    entityId,
    campaign: { campaignId: campaign?.id, campaignType: campaign?.campaign_type },
    dateRange: { from: fromDate, to: toDate },
    enabled: true,
  });

  return {
    category,
    chartData: chartData ?? [],
    isLoading,
    onGoBack:
      fromDate < earliestDate
        ? undefined
        : () => {
            const newOffset = daysOffset + CHART_TIME_SPAN;
            setDaysOffset(newOffset);
            trackCampaignPerformanceInteractionClickArrow(newOffset);
          },
    onGoForward:
      toDate >= latestDate
        ? undefined
        : () => {
            const newOffset = daysOffset - CHART_TIME_SPAN;
            setDaysOffset(newOffset);
            trackCampaignPerformanceInteractionClickArrow(newOffset);
          },
    onChangeCategory: (category: ChartCategory) => {
      setCategory(category);
      trackCampaignPerformanceInteractionClickSalesOrOrder(category);
    },
  };
};
